import { useMemo, useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Eye, EyeOff, Inbox, Info, Loader2, Plus, ShieldCheck, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  externalGrants,
  type RemoteAccessGrantRow,
  type RemoteAccessRole,
  type RemoteAccessScope,
  type RemoteAccessStatus,
} from '@/api/client';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { ActionMenu, ActionItem } from '@/components/ui/ActionMenu';
import { NewExternalGrantDialog } from './NewExternalGrantDialog';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-11 — Coordinator-side external-access management panel.
 *
 * Embedded inside `BinderDetailPage` (visibility toggled from the page
 * header). Shows every external-reviewer grant on the binder + lets the
 * coordinator issue a new one or revoke an existing one.
 *
 * Behaviour:
 *   - useQuery externalGrants.list(binderId, includeRevoked) — the
 *     "Include revoked" checkbox toggles the second arg.
 *   - Table columns: name, email, role badge, scope badge, valid range,
 *     status badge, last_login_at, action menu (revoke).
 *   - "+ Grant external access" opens the NewExternalGrantDialog modal.
 *   - Revoke from the action menu opens a small inline modal asking for a
 *     reason (BE requires 4-2000 chars). On success the row drops out of
 *     the default list (or re-renders as `revoked` if the checkbox is on).
 */

const ROLE_LABEL: Record<RemoteAccessRole, string> = {
  sponsor_monitor: 'Sponsor monitor',
  auditor: 'Auditor',
};

const SCOPE_LABEL: Record<RemoteAccessScope, string> = {
  view_full_binder: 'Full binder',
  view_pii_redacted: 'PII redacted',
  view_specific_folders: 'Specific folders',
};

function roleBadgeClass(role: RemoteAccessRole): string {
  return role === 'auditor' ? 'badge badge-warning' : 'badge badge-info';
}

function scopeBadgeClass(scope: RemoteAccessScope): string {
  switch (scope) {
    case 'view_full_binder':
      return 'badge badge-danger';
    case 'view_pii_redacted':
      return 'badge badge-neutral';
    case 'view_specific_folders':
      return 'badge badge-info';
  }
}

function statusBadgeClass(status: RemoteAccessStatus): string {
  switch (status) {
    case 'active':
      return 'badge badge-success';
    case 'pending':
      return 'badge badge-info';
    case 'expired':
      return 'badge badge-neutral';
    case 'revoked':
      return 'badge badge-danger';
  }
}

interface RevokeDialogState {
  grant: RemoteAccessGrantRow;
}

export interface ExternalGrantsTabProps {
  binderId: string;
  /** Punch-list DT2-BLIND (Spike F fix) — passed through to the create-grant dialog. */
  studyId?: string;
}

export function ExternalGrantsTab({ binderId, studyId }: ExternalGrantsTabProps) {
  const qc = useQueryClient();

  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [revokeDialog, setRevokeDialog] = useState<RevokeDialogState | null>(null);

  const grantsQ = useQuery({
    queryKey: ['external-grants', binderId, includeRevoked],
    queryFn: () => externalGrants.list(binderId, includeRevoked),
    enabled: !!binderId,
  });

  const rows = useMemo(() => grantsQ.data?.grants ?? [], [grantsQ.data]);

  return (
    <div className="space-y-4">
      <div className="card p-4">
        {/* Header row */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-ink-primary">External access</h2>
            {grantsQ.data && (
              <span className="text-xs text-ink-muted">
                ({rows.length} grant{rows.length === 1 ? '' : 's'})
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-ink-muted">
              <input
                type="checkbox"
                checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
              />
              {includeRevoked ? (
                <Eye className="h-3.5 w-3.5" />
              ) : (
                <EyeOff className="h-3.5 w-3.5" />
              )}
              Include revoked
            </label>
            <button type="button" className="btn-primary text-xs" onClick={() => setNewOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Grant external access
            </button>
          </div>
        </div>

        {/* States */}
        {grantsQ.isLoading && (
          <div className="flex items-center justify-center gap-2 py-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading grants…
          </div>
        )}
        {grantsQ.isError && (
          <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${errorToneClass(grantsQ.error)}`}>
            {isPermissionError(grantsQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {errorText(grantsQ.error, 'Failed to load external grants.')}
            </span>
          </div>
        )}
        {grantsQ.data && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-ink-muted">
            <Inbox className="h-6 w-6 opacity-50" />
            <span>
              No {includeRevoked ? '' : 'active '}external-access grants on this binder yet.
            </span>
            <button type="button" className="btn-ghost text-xs" onClick={() => setNewOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> Grant external access
            </button>
          </div>
        )}

        {/* Table */}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-3 py-2">Reviewer</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Scope</th>
                  <th className="px-3 py-2">Validity</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Last login</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <ExternalGrantRow
                    key={r.grant_id}
                    row={r}
                    onRevoke={() => setRevokeDialog({ grant: r })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New-grant dialog */}
      <NewExternalGrantDialog
        binderId={binderId}
        studyId={studyId}
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['external-grants', binderId] });
        }}
      />

      {/* Revoke confirmation dialog */}
      {revokeDialog && (
        <RevokeGrantDialog
          binderId={binderId}
          grant={revokeDialog.grant}
          onClose={() => setRevokeDialog(null)}
          onRevoked={() => {
            setRevokeDialog(null);
            qc.invalidateQueries({ queryKey: ['external-grants', binderId] });
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// Row + revoke dialog
// =====================================================================

function ExternalGrantRow({ row, onRevoke }: { row: RemoteAccessGrantRow; onRevoke: () => void }) {
  const canRevoke = row.status !== 'revoked';
  return (
    <tr className="border-b border-surface-border last:border-0">
      <td className="px-3 py-2 text-xs">
        <div className="font-medium text-ink-primary">{row.granted_to_name}</div>
        <div className="text-[11px] text-ink-muted">{row.granted_to_email}</div>
      </td>
      <td className="px-3 py-2 text-xs">
        <span className={roleBadgeClass(row.granted_to_role)}>
          {ROLE_LABEL[row.granted_to_role]}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">
        <span className={scopeBadgeClass(row.scope)}>{SCOPE_LABEL[row.scope]}</span>
      </td>
      <td className="px-3 py-2 text-xs">
        <div>{fmtDate(row.valid_from)}</div>
        <div className="text-[11px] text-ink-muted">→ {fmtDate(row.valid_to)}</div>
      </td>
      <td className="px-3 py-2 text-xs">
        <span className={statusBadgeClass(row.status)}>{row.status}</span>
        {row.status === 'revoked' && row.revocation_reason && (
          <div
            className="mt-0.5 max-w-[200px] truncate text-[10px] text-ink-muted"
            title={row.revocation_reason}
          >
            {row.revocation_reason}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {row.last_login_at ? (
          fmtDateTime(row.last_login_at)
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {canRevoke && (
          <ActionMenu>
            <ActionItem icon={Trash2} danger onClick={onRevoke}>
              Revoke access
            </ActionItem>
          </ActionMenu>
        )}
      </td>
    </tr>
  );
}

interface RevokeGrantDialogProps {
  binderId: string;
  grant: RemoteAccessGrantRow;
  onClose: () => void;
  onRevoked: () => void;
}

function RevokeGrantDialog({ binderId, grant, onClose, onRevoked }: RevokeGrantDialogProps) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const isValid = trimmed.length >= 4 && trimmed.length <= 2000;

  const revoke = useMutation({
    mutationFn: () => externalGrants.revoke(binderId, grant.grant_id, { reason: trimmed }),
    onSuccess: () => {
      toast.success(`Revoked access for ${grant.granted_to_name}`);
      onRevoked();
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to revoke grant'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid || revoke.isPending) return;
    revoke.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !revoke.isPending) onClose();
      }}
    >
      <form onSubmit={handleSubmit} className="card w-full max-w-md space-y-4 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">Revoke external access</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              This immediately invalidates the reviewer's magic link and any active session.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={revoke.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-md border border-surface-border bg-surface-muted/40 p-3 text-xs">
          <div className="font-medium text-ink-primary">{grant.granted_to_name}</div>
          <div className="text-ink-muted">{grant.granted_to_email}</div>
          <div className="mt-1 flex items-center gap-2">
            <span className={roleBadgeClass(grant.granted_to_role)}>
              {ROLE_LABEL[grant.granted_to_role]}
            </span>
            <span className={scopeBadgeClass(grant.scope)}>{SCOPE_LABEL[grant.scope]}</span>
          </div>
        </div>

        <div>
          <label className="label text-xs" htmlFor="revoke-reason">
            Reason *
          </label>
          <textarea
            id="revoke-reason"
            className="input min-h-[88px]"
            maxLength={2000}
            value={reason}
            placeholder="e.g. Monitor visit completed; access no longer required."
            disabled={revoke.isPending}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          <p className="mt-1 text-[11px] text-ink-muted">
            Recorded verbatim in the audit trail. 4–2000 characters.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={revoke.isPending}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || revoke.isPending}>
            {revoke.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Revoke access
          </button>
        </div>
      </form>
    </div>
  );
}

export default ExternalGrantsTab;
